import React from 'react';
import ProductInstanceModifyTableView from '../../views/product-instance/modify-table';
import { ProductInstanceCollection, CustomerCollection, LocationCollection, ProductVersionsCollection } from '../../services/collection';
import { withRouter } from 'react-router-dom';
import { requestUpdate } from '../../services/update-service';

class ProductInstanceModifyTableScreen extends React.Component {

  state = {
    batch: false,
    selected: [],
    filter: '',
    disabled: false,
    freeTextProductVersion: false
  };

  onToggleBatch = () => this.setState(({ batch }) => ({
    batch: !batch,
    selected: [],
    editing: null
  }));

  onProvision = () => {
    const { history } = this.props;
    history.push('/pending-product-instance/provision');
  };

  onChange = editing => this.setState({
    editing
  });

  onSelect = (uuid, product) => this.setState(({ batch, selected }) => ({
    selected: (batch ? selected : []).concat(product),
    editing: null
  }));

  onUnselect = (uuid) => this.setState(({ selected }) => ({
    selected: selected
      .filter(other => other.uuid !== uuid),
    editing: null
  }));

  onFilter = filter => this.setState({
    filter
  });

  onSave = async () => {
    if (!this.isValid()) {
      return null;
    }
    const { batch, editing } = this.state;
    let products = [editing];
    if (batch) {
      products = this.state.selected
        .map(product => ({
          ...product,
          ...editing
        }));
    }
    await new Promise(resolve => this.setState({
      disabled: true
    }, resolve));
    try {
      await Promise.all(
        products.map(
          product => ProductInstanceCollection.actions.save(product)
        )
      );
      await new Promise(resolve => this.setState({
        disabled: false,
        selected: [],
        editing: null
      }, resolve));
    } catch(e) {
      console.warn(e);
      alert(`Failed to save product${products.length > 1 ? 's' : ''}`);
      await new Promise(resolve => this.setState({
        disabled: false,
      }, resolve));
    }
  };

  onCancel = () => this.setState({
    selected: [],
    editing: null
  });

  onPush = async () => {
    const { selected, editing } = this.state;
    if (!selected.length || editing) {
      return null;
    }
    await new Promise(resolve => this.setState({
      disabled: true
    }, resolve));
    try {
      await Promise.all(
        selected.map(({ uuid }) => requestUpdate(uuid))
      );
    } catch(e) {
      console.warn(e);
      alert(`Failed to request update for product${selected.length ? 's' : ''}`);
    } finally {
      await new Promise(resolve => this.setState({
        disabled: false,
      }, resolve));
    }
  };

  isValid() {
    const { batch, editing } = this.state;
    if (!editing) {
      // No changes have yet been made
      return false;
    }
    const {
      parentUuid,
      productVersion
    } = editing;
    if (batch) {
      return !!(
        parentUuid &&
        productVersion
      );
    }
    const {
      productId,
      systemIdentifier,
      systemStatus
    } = editing;
    return !!(
      parentUuid &&
      productVersion &&
      productId &&
      systemIdentifier &&
      systemStatus
    );
  }

  getEditingProduct() {
    const { batch, selected, editing } = this.state;
    if (!selected.length) {
      return null;
    }
    if (editing) {
      return editing;
    }
    if (batch) {
      return {};
    }
    return {
      parentUuid: '',
      productId: '',
      productVersion: '',
      systemIdentifier: '',
      jurisdiction: '',
      systemStatus: '',
      description: '',
      paidThrough: 0,
      ...(selected[0] || {})
    }

  }

  static getDefaultVersions() {
    return {
      'com.rewardsinc:location-manager-app': ['1.0.0'],
      'com.rewardsinc:core-data-app': ['1.0.0'],
      'com.rewardsinc:site-controller-app': ['1.0.0'],
      'com.rewardsinc:update-service-app': ['1.0.0']
    }
  }

  static getVersions(versions) {
    if (!(versions && typeof versions === 'object')) {
      return ProductInstanceModifyTableScreen.getDefaultVersions();
    }
    const keys = Object.keys(versions);
    if (!keys.length) {
      return ProductInstanceModifyTableScreen.getDefaultVersions();
    }
    const keyWithVersions = keys.find(key => Array.isArray(versions[key]) && versions[key].length);
    return keyWithVersions ? versions : ProductInstanceModifyTableScreen.getDefaultVersions();
  }

  onToggleFreeTextProductVersion = () => this.setState(({ freeTextProductVersion }) => ({
    freeTextProductVersion: !freeTextProductVersion
  }));

  onDelete = async (data, remove) => {
    await ProductInstanceCollection.actions.remove(data.uuid);
    await remove(data.uuid);
  };

  render() {
    const { onToggleBatch, onProvision, onChange, onSelect, onUnselect, onFilter, onSave, onCancel, onPush, onToggleFreeTextProductVersion, onDelete } = this;
    const { batch, selected, filter, disabled, editing, freeTextProductVersion } = this.state;
    const { loading, list = [], customers = [], locations = [], versions } = this.props;
    return (
      <ProductInstanceModifyTableView
        freeTextProductVersion={freeTextProductVersion}
        onToggleFreeTextProductVersion={onToggleFreeTextProductVersion}
        disabled={disabled || loading}
        batch={batch}
        onToggleBatch={onToggleBatch}
        onProvision={onProvision}
        onChange={onChange}
        product={selected.length ? this.getEditingProduct() : null}
        products={selected}
        filter={filter}
        items={list}
        onSelect={onSelect}
        onUnselect={onUnselect}
        onFilter={onFilter}
        onSave={onSave}
        onCancel={onCancel}
        onPush={onPush}
        canSave={this.isValid()}
        onDelete={onDelete}
        canPush={!!(selected.length && !editing)}
        customers={customers}
        locations={locations}
        jurisdictions={[
          {
            key: 'MT',
            name: 'Montanta'
          },
          {
            key: 'IL',
            name: 'Illinois'
          }
        ]}
        systemStatuses={[
          'ACTIVE',
          'INACTIVE',
          'STOLEN'
        ]}
        versions={ProductInstanceModifyTableScreen.getVersions(versions)}
      />
    )
  }

}

const mapVersionsProps = ({ loading, list }) => ({ versions: list, versionsLoading: loading });
const mapCustomerProps = ({ loading, list }) => ({ customers: list, customersLoading: loading });
const mapLocationProps = ({ loading, list }) => ({ locations: list, locationsLoading: loading });
const mapProductProps = ({ loading, list }) => ({ list, loading });

const versionsConnect = ProductVersionsCollection.connect(mapVersionsProps);
const customerConnect = CustomerCollection.connect(mapCustomerProps);
const locationConnect = LocationCollection.connect(mapLocationProps);
const productInstanceConnect = ProductInstanceCollection.connect(mapProductProps);

export default versionsConnect(
  customerConnect(
    locationConnect(
      productInstanceConnect(
        withRouter(ProductInstanceModifyTableScreen)
      )
    )
  )
);



// WEBPACK FOOTER //
// ./src/app/product-instance/index.js